import { useCallback, useEffect, useMemo, useState } from "react";
import { TaskTemplate } from "@common/types/taskTemplate";
import { useCloseTemplates } from "@api/closeTemplate.api";
import { useOrganizationCloseStages } from "@api/organizationCloseStage.api";
import { useCloseTemplateTaskTemplates } from "@api/taskTemplate.api";
import {
    computeHasUnsavedChanges,
    useCloseTemplateTasksSave,
} from "./useCloseTemplateTasksSave";
import { LocalTask } from "./closeTemplateTasksDrawer.types";

const taskToLocal = (task: TaskTemplate): LocalTask => ({
    localId: task.id,
    persistedId: task.id,
    name: task.title,
    closeStageId: task.closeStageId,
    assigneeRole: task.assigneeRole,
    assigneeId: task.assigneeId,
    externalDeadline: task.externalDeadline,
    deadlineAnchor: task.deadlineAnchor,
    estimatedTime: task.estimatedTime,
    taskType: task.type,
    initialMessage: task.initialMessage,
    viewConfig: task.viewConfig ?? null,
    sourceTemplateId: task.sourceTemplateId,
    sourceTemplateVersion: task.sourceTemplateVersion,
    isDeleted: false,
    order: task.order,
    blockedByLocalIds: [...task.blockedByTaskTemplateIds],
    relatedLocalIds: [...task.relatedTaskTemplateIds],
});

interface UseCloseTemplateTasksArgs {
    closeTemplateId: string | null;
    /** Whether to fetch task data (set to false to skip fetching when the drawer is closed). */
    isFetchEnabled: boolean;
    onClose: () => void;
}

export const useCloseTemplateTasks = ({
    closeTemplateId,
    isFetchEnabled,
    onClose,
}: UseCloseTemplateTasksArgs) => {
    const [localTasks, setLocalTasks] = useState<LocalTask[]>([]);
    const [isConfirmDiscardOpen, setIsConfirmDiscardOpen] = useState(false);
    const [isApplyDrawerOpen, setIsApplyDrawerOpen] = useState(false);

    const { data: closeTemplates } = useCloseTemplates();
    const { data: stages } = useOrganizationCloseStages();
    const { data: fetchedTasks, isFetched } = useCloseTemplateTaskTemplates(
        isFetchEnabled ? closeTemplateId : null,
    );

    const currentTemplate = useMemo(
        () =>
            closeTemplateId
                ? (closeTemplates ?? []).find((t) => t.id === closeTemplateId)
                : undefined,
        [closeTemplateId, closeTemplates],
    );

    const isReady =
        isFetchEnabled && !!closeTemplateId && isFetched && !!stages;

    useEffect(() => {
        if (!isReady) {
            return;
        }
        setLocalTasks((fetchedTasks ?? []).map(taskToLocal));
    }, [isReady, fetchedTasks, closeTemplateId]);

    const handleOpenApplyDrawer = useCallback(() => {
        setIsApplyDrawerOpen(true);
    }, []);

    const isDirty = useMemo(
        () => computeHasUnsavedChanges(fetchedTasks ?? [], localTasks),
        [fetchedTasks, localTasks],
    );

    const { handleSave, handleSaveAndApply, isSaving } =
        useCloseTemplateTasksSave({
            closeTemplateId,
            closeTemplateName: currentTemplate?.name,
            fetchedTasks: fetchedTasks ?? [],
            localTasks,
            onSuccess: onClose,
            onSaveAndApply: handleOpenApplyDrawer,
        });

    const handleLocalTasksChange = useCallback(
        (updater: (prev: LocalTask[]) => LocalTask[]) => {
            setLocalTasks((prev) => updater(prev));
        },
        [],
    );

    const handleCloseAttempt = useCallback(
        (cancel: () => void) => {
            if (computeHasUnsavedChanges(fetchedTasks ?? [], localTasks)) {
                cancel();
                setIsConfirmDiscardOpen(true);
            }
        },
        [fetchedTasks, localTasks],
    );

    const handleConfirmDiscard = useCallback(() => {
        setIsConfirmDiscardOpen(false);
        onClose();
    }, [onClose]);

    const handleSaveAndExit = useCallback(() => {
        setIsConfirmDiscardOpen(false);
        void handleSave();
    }, [handleSave]);

    const handleKeepEditing = useCallback(() => {
        setIsConfirmDiscardOpen(false);
    }, []);

    return {
        localTasks,
        stages,
        currentTemplate,
        isReady,
        isDirty,
        isSaving,
        isConfirmDiscardOpen,
        isApplyDrawerOpen,
        setIsApplyDrawerOpen,
        handleLocalTasksChange,
        handleSave,
        handleSaveAndApply,
        handleOpenApplyDrawer,
        handleCloseAttempt,
        handleConfirmDiscard,
        handleSaveAndExit,
        handleKeepEditing,
    };
};
